(() => {
  const dialog = document.getElementById('skull-size-finder');
  if (!dialog || dialog.dataset.initialized) return;
  dialog.dataset.initialized = 'true';

  const form = dialog.querySelector('[data-size-finder-form]');
  const result = dialog.querySelector('[data-size-finder-result]');
  const error = dialog.querySelector('[data-size-finder-error]');
  const sizeOutputs = [...dialog.querySelectorAll('[data-size-finder-size]')];
  const sizeNameOutputs = [...dialog.querySelectorAll('[data-size-finder-name]')];
  const inputStep = dialog.querySelector('[data-size-finder-input-step]');
  const configNode = dialog.querySelector('[data-size-finder-config]');
  const overlay = document.querySelector('[data-size-finder-overlay]');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointer = matchMedia('(pointer: coarse)');
  let config;
  let returnFocus = null;
  let closing = false;
  let openTimer = 0;
  let audioContext;
  let lastTickAt = 0;

  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }

  const nearestIndex = (values, target) => values.reduce((best, value, index) => Math.abs(value - target) < Math.abs(values[best] - target) ? index : best, 0);
  const weightIndex = weight => config.weightBands.findIndex(([minimum, maximum]) => weight >= minimum && weight <= maximum);
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const sizeOrder = { CH: 0, M: 1, G: 2, XG: 3, XXG: 4 };
  const distanceToBand = (weight, [minimum, maximum]) => weight < minimum ? minimum - weight : weight > maximum ? weight - maximum : 0;

  function recommendSize(height, weight) {
    const nearestRow = nearestIndex(config.heights, height);
    const exactBand = weightIndex(weight);
    const exactSize = exactBand >= 0 ? config.matrix[nearestRow]?.[exactBand] : '';
    if (exactSize) return exactSize;

    let best = null;
    config.matrix.forEach((row, rowIndex) => row.forEach((size, bandIndex) => {
      if (!size) return;
      const heightDistance = Math.abs(height - config.heights[rowIndex]) / 3;
      const weightDistance = distanceToBand(weight, config.weightBands[bandIndex]) / 10;
      const score = (heightDistance ** 2) + (weightDistance ** 2);
      const combinedDistance = heightDistance + weightDistance;
      const candidate = { size, score, combinedDistance };
      if (!best
        || candidate.score < best.score - .0001
        || (Math.abs(candidate.score - best.score) < .0001 && candidate.combinedDistance < best.combinedDistance - .0001)
        || (Math.abs(candidate.score - best.score) < .0001 && Math.abs(candidate.combinedDistance - best.combinedDistance) < .0001 && sizeOrder[size] > sizeOrder[best.size])) best = candidate;
    }));
    return best?.size || '';
  }

  function resetResult() {
    dialog.classList.remove('is-result');
    inputStep.hidden = false;
    error.hidden = true;
    error.textContent = '';
    result.hidden = true;
  }

  function prepareAudio() {
    if (coarsePointer.matches) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    audioContext ||= new AudioContext();
    if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
  }

  function tick() {
    if (coarsePointer.matches) {
      navigator.vibrate?.(5);
      return;
    }
    const now = performance.now();
    if (now - lastTickAt < 48) return;
    lastTickAt = now;
    try {
      prepareAudio();
      if (!audioContext || audioContext.state !== 'running') return;
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(760, audioContext.currentTime);
      gain.gain.setValueAtTime(.012, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(.0001, audioContext.currentTime + .014);
      oscillator.connect(gain).connect(audioContext.destination);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
      oscillator.start();
      oscillator.stop(audioContext.currentTime + .015);
    } catch (_) {}
  }

  function buildWheel(wheel) {
    const optionsRoot = wheel.querySelector('[data-size-wheel-options]');
    const shell = wheel.closest('.skull-size-finder__wheel-shell');
    const input = wheel.closest('label').querySelector('input[type="hidden"]');
    const stepButtons = [...shell.querySelectorAll('[data-size-wheel-step]')];
    const minimum = Number(wheel.dataset.min);
    const maximum = Number(wheel.dataset.max);
    const unit = wheel.dataset.unit || '';
    const rowHeight = Number.parseFloat(getComputedStyle(wheel).getPropertyValue('--wheel-row')) || 38;
    const buttons = [];
    let selectedIndex = clamp(Number(wheel.dataset.value) - minimum, 0, maximum - minimum);
    let lastSoundIndex = selectedIndex;
    let interacting = false;
    let mounted = false;
    let frame = 0;
    let settleTimer = 0;

    for (let value = minimum; value <= maximum; value += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'skull-size-finder__wheel-option';
      button.dataset.value = String(value);
      button.setAttribute('role', 'option');
      button.setAttribute('tabindex', '-1');
      button.innerHTML = `<span>${value}</span><small>${unit}</small>`;
      optionsRoot.append(button);
      buttons.push(button);
    }

    const scrollPosition = index => buttons[index].offsetTop - ((wheel.clientHeight - buttons[index].offsetHeight) / 2);
    const indexFromScroll = () => clamp(Math.round((wheel.scrollTop - scrollPosition(0)) / rowHeight), 0, buttons.length - 1);

    function select(index, { sound = false, align = false, behavior = 'auto' } = {}) {
      index = clamp(index, 0, buttons.length - 1);
      const changed = index !== selectedIndex;
      selectedIndex = index;
      const value = minimum + index;
      input.value = String(value);
      wheel.dataset.value = String(value);
      wheel.setAttribute('aria-activedescendant', `${input.id}-option-${value}`);
      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-selected', String(active));
        button.id = `${input.id}-option-${minimum + buttonIndex}`;
      });
      stepButtons.forEach(button => {
        const direction = Number(button.dataset.sizeWheelStep);
        button.disabled = direction < 0 ? index === 0 : index === buttons.length - 1;
      });
      if (align) wheel.scrollTo({ top: scrollPosition(index), behavior });
      if (changed) {
        resetResult();
        if (sound) {
          tick();
          lastSoundIndex = index;
        }
      }
    }

    function settleScroll() {
      const settledIndex = indexFromScroll();
      select(settledIndex);
      const targetTop = scrollPosition(settledIndex);
      if (Math.abs(wheel.scrollTop - targetTop) > .75) wheel.scrollTo({ top: targetTop, behavior: 'auto' });
      if (interacting && settledIndex !== lastSoundIndex) {
        tick();
        lastSoundIndex = settledIndex;
      }
      interacting = false;
    }

    function syncFromScroll() {
      frame = 0;
      select(indexFromScroll());
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settleScroll, coarsePointer.matches ? 160 : 110);
    }

    wheel.addEventListener('scroll', () => {
      if (!mounted) return;
      if (!frame) frame = requestAnimationFrame(syncFromScroll);
    }, { passive: true });
    wheel.addEventListener('pointerdown', () => { interacting = true; prepareAudio(); }, { passive: true });
    wheel.addEventListener('wheel', () => { interacting = true; prepareAudio(); }, { passive: true });
    if ('onscrollend' in wheel) wheel.addEventListener('scrollend', () => {
      clearTimeout(settleTimer);
      settleScroll();
    }, { passive: true });
    wheel.addEventListener('keydown', event => {
      const directions = { ArrowUp: -1, ArrowDown: 1, PageUp: -3, PageDown: 3 };
      if (!(event.key in directions)) return;
      event.preventDefault();
      interacting = true;
      prepareAudio();
      select(selectedIndex + directions[event.key], { sound: true, align: true, behavior: coarsePointer.matches || reducedMotion.matches ? 'auto' : 'smooth' });
    });
    optionsRoot.addEventListener('click', event => {
      const button = event.target.closest('button[data-value]');
      if (!button) return;
      interacting = true;
      prepareAudio();
      select(Number(button.dataset.value) - minimum, { sound: true, align: true, behavior: coarsePointer.matches || reducedMotion.matches ? 'auto' : 'smooth' });
    });
    stepButtons.forEach(button => button.addEventListener('click', () => {
      interacting = true;
      prepareAudio();
      select(selectedIndex + Number(button.dataset.sizeWheelStep), { sound: true, align: true, behavior: coarsePointer.matches || reducedMotion.matches ? 'auto' : 'smooth' });
    }));

    select(selectedIndex);
    return {
      mount() {
        mounted = true;
        select(selectedIndex, { align: true });
      }
    };
  }

  const wheelControllers = [...dialog.querySelectorAll('[data-size-wheel]')].map(buildWheel);

  async function close() {
    if (!dialog.open || closing) return;
    closing = true;
    if (!reducedMotion.matches && dialog.animate) {
      const animation = dialog.animate(
        [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(calc(100% + 18px))', opacity: .35 }],
        { duration: 220, easing: 'cubic-bezier(.4,0,.8,.2)' }
      );
      await animation.finished.catch(() => {});
    }
    dialog.close();
    closing = false;
  }

  function open(attempt = 0) {
    clearTimeout(openTimer);
    if (dialog.open) return;
    const otherDialog = document.querySelector('dialog[open]');
    const otherSheet = document.querySelector('.pvg-refined-modal.active,[class^="skull-pvg-modal-"].active');
    if ((otherDialog || otherSheet) && attempt < 20) {
      openTimer = setTimeout(() => open(attempt + 1), 80);
      return;
    }
    if (otherDialog || otherSheet) return;
    returnFocus ||= document.activeElement;
    resetResult();
    dialog.show();
    document.body.classList.add('skull-size-finder-open');
    dialog.scrollTop = 0;
    wheelControllers.forEach(controller => controller.mount());
    if (!reducedMotion.matches && dialog.animate) {
      dialog.animate(
        [{ transform: 'translateY(calc(100% + 18px))', opacity: .35 }, { transform: 'translateY(0)', opacity: 1 }],
        { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' }
      );
    }
  }

  form.addEventListener('submit', event => {
    event.preventDefault();
    resetResult();
    const data = new FormData(form);
    const height = Number(data.get('height'));
    const weight = Number(data.get('weight'));
    const size = recommendSize(height, weight);

    if (!size) {
      error.textContent = config.copy.noMatch;
      error.hidden = false;
      return;
    }

    sizeOutputs.forEach(output => { output.textContent = size; });
    sizeNameOutputs.forEach(output => { output.textContent = config.sizeNames[size] || size; });
    inputStep.hidden = true;
    dialog.classList.add('is-result');
    result.hidden = false;
    result.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    document.dispatchEvent(new CustomEvent('skull:size-recommended', { detail: { height, weight, size } }));
  });

  function requestOpen(trigger) {
    returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    const drawer = returnFocus?.closest?.('header-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    document.dispatchEvent(new CustomEvent('skull:size-finder-request', { detail: { trigger: returnFocus } }));
    clearTimeout(openTimer);
    openTimer = setTimeout(() => open(), drawer ? 360 : 40);
  }
  window.SKULL_SIZE_FINDER_OPEN = requestOpen;
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element) || dialog.contains(event.target)) return;
    const explicit = event.target.closest('[data-size-finder-open]');
    const link = event.target.closest('a[href]');
    const summary = event.target.closest('summary');
    let guideLink = false;
    if (link) {
      const url = new URL(link.href, location.href);
      guideLink = url.pathname.replace(/\/$/, '') === '/pages/guia-de-tallas';
    }
    const summaryText = summary?.textContent?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() || '';
    const guideSummary = summaryText.includes('guia de tallas');
    const trigger = explicit || (guideLink ? link : null) || (guideSummary ? summary : null);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestOpen(trigger);
  }, true);
  dialog.querySelector('[data-size-finder-close]').addEventListener('click', close);
  overlay?.addEventListener('click', close);
  dialog.querySelector('[data-size-finder-result-back]').addEventListener('click', () => {
    resetResult();
    requestAnimationFrame(() => dialog.querySelector('[data-size-wheel]')?.focus({ preventScroll: true }));
  });
  dialog.querySelector('[data-size-finder-result-close]').addEventListener('click', close);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && dialog.open) close();
  });
  dialog.addEventListener('close', () => {
    document.body.classList.remove('skull-size-finder-open');
    returnFocus?.focus?.({ preventScroll: true });
    returnFocus = null;
  });
  window.addEventListener('pagehide', () => clearTimeout(openTimer));
})();
